/**
 * Who is signed in (FR-ORG-03). E-mail + password, then an authenticator code where the
 * role requires it (admins, records officers, auditors). A first sign-in asks for a new
 * password and, for privileged roles, authenticator enrolment before anything else.
 * Permissions and the organisation come from the session payload (auth/me shape).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, baseHeaders, refreshSession, setAccessToken, setUnauthenticatedHandler } from './api';
import { EP } from './endpoints';
import type { SessionResponse, SessionUser } from './types';

export type { SessionResponse, SessionUser } from './types';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  /** Why the last session ended, shown once on the sign-in screen. */
  endedReason: 'idle' | 'signed-out' | 'expired' | null;
  adopt: (r: SessionResponse) => void;
  reissue: () => Promise<SessionUser>;
  signOut: (reason?: 'idle' | 'signed-out') => Promise<void>;
  /** True when the user holds ANY of the given permissions. */
  can: (...permissions: string[]) => boolean;
  /** Move the session to another organisation (platform admin / multi-org users). */
  switchOrg: (orgId: string | null) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

/** Partial payloads still produce a usable user (defensive against contract drift). */
export function normalizeUser(u: Partial<SessionUser> & { id: string; name: string }): SessionUser {
  return {
    email: '',
    roleCode: '',
    roleName: '',
    permissions: [],
    org: null,
    orgs: [],
    platformAdmin: false,
    mfaEnabled: false,
    mfaRequired: false,
    mustChangePassword: false,
    ...u,
  } as SessionUser;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [endedReason, setEndedReason] = useState<AuthState['endedReason']>(null);
  const qc = useQueryClient();

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    qc.clear();
  }, [qc]);

  const adopt = useCallback((r: SessionResponse) => {
    setAccessToken(r.accessToken);
    setUser(normalizeUser(r.user));
    setEndedReason(null);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setEndedReason((prev) => prev ?? 'expired');
      clear();
    });
    // Review H2: the shared single-flight refresh (also used on 401s, serialised across tabs).
    void refreshSession<SessionResponse>()
      .then((r) => (r ? adopt(r) : undefined))
      .catch(() => undefined)
      .finally(() => setLoading(false));
    return () => setUnauthenticatedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reissue = useCallback(async () => {
    const r = await api<SessionResponse>(EP.auth.reissue, { method: 'POST', body: {} });
    adopt(r);
    return normalizeUser(r.user);
  }, [adopt]);

  const signOut = useCallback(
    async (reason: 'idle' | 'signed-out' = 'signed-out') => {
      await fetch(`/api/v1${EP.auth.logout}`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...baseHeaders(), 'content-type': 'application/json' },
        body: '{}',
      }).catch(() => undefined);
      setEndedReason(reason);
      clear();
    },
    [clear],
  );

  const switchOrg = useCallback(
    async (orgId: string | null) => {
      const r = await api<SessionResponse>(EP.auth.switchOrg, { method: 'POST', body: { orgId } });
      qc.clear();
      adopt(r);
    },
    [adopt, qc],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      endedReason,
      adopt,
      reissue,
      signOut,
      // No role short-cut: even an Organisation Admin only gets what the API grants
      // (PRD §6 — site and client scope come from role assignments). The API checks every call.
      can: (...perms) => !!user && (perms.length === 0 || perms.some((p) => user.permissions.includes(p))),
      switchOrg,
    }),
    [user, loading, endedReason, adopt, reissue, signOut, switchOrg],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
